import React from "react";
import theme from "../data/theme";
import { captureSetupNudgeClicked } from "../utils/onboardingAnalytics";

/**
 * The nudge that turns a DEFAULT chip into an invitation. Renders NOTHING once
 * a league exists — the league name replacing DEFAULT is the payoff, and a
 * standing "set up" link next to a league you already set up is noise.
 *
 * Opens the existing settings/import flow (the same handler behind the header
 * selector's "+ Import League"); it deliberately owns no modal or route.
 */
export const SetupNudge = React.memo(function SetupNudge({ name, onSetup, source, style }) {
  if (name || !onSetup) return null;
  return (
    <span
      onClick={() => { captureSetupNudgeClicked(source); onSetup(); }}
      style={{
        color: theme.colors.brand.orange, cursor: "pointer", fontSize: 13, fontWeight: 700,
        whiteSpace: "nowrap", ...style,
      }}
    >Set up your league &rarr;</span>
  );
});

/**
 * The active league, as a filled chip. One component so the league reads the
 * same everywhere it appears (header summary, mock setup format line, …).
 *
 * Never renders nothing: with no league loaded it shows a muted DEFAULT chip,
 * because "no chip" and "chip you scrolled past" look identical, and the whole
 * point is that settings/opponents/tiers are per-league now.
 *
 * size "sm" (default) is the inline size for dense summary lines; "lg" is for
 * the standalone context strip, where the chip has to carry a 16px line.
 */
const LeagueChip = React.memo(function LeagueChip({ name, style, size = "sm" }) {
  const isDefault = !name;
  const lg = size === "lg";
  return (
    <span style={{
      display: "inline-block", verticalAlign: "middle",
      padding: lg ? "3px 10px" : "2px 8px", borderRadius: 4,
      fontSize: lg ? 13 : 10, fontWeight: 800, letterSpacing: "0.5px",
      lineHeight: 1.4, whiteSpace: "nowrap",
      background: isDefault ? "rgba(255,255,255,0.08)" : theme.colors.brand.orange,
      color: isDefault ? theme.colors.text.muted : theme.colors.bg.app,
      ...style,
    }}>
      {isDefault ? "DEFAULT" : name.toUpperCase()}
    </span>
  );
});

/**
 * "This draft is priced off your league's own auctions" badge, for the header
 * summary row during a live mock.
 *
 * Outlined rather than filled on purpose: LeagueChip sits inches away in the
 * same row carrying the league identity in solid orange, and a second solid
 * orange chip repeating the league name would read as a duplicate rather than
 * a qualifier. This is the same treatment as the header's ALMANAC "BETA"
 * badge — a quiet annotation on something else, which is exactly what this is.
 *
 * nowrap because the row that holds it is a single fixed-height line that
 * ellipsizes; a wrapping badge would be clipped mid-word instead of cleanly.
 */
export const CalibratedChip = React.memo(function CalibratedChip({ name, style }) {
  if (!name) return null;
  return (
    <span style={{
      display: "inline-block", verticalAlign: "middle", marginLeft: 6,
      fontSize: theme.fontSize.nano, fontWeight: theme.fontWeight.heavy,
      letterSpacing: "0.5px", lineHeight: 1.4, whiteSpace: "nowrap",
      color: theme.colors.brand.orange,
      background: theme.helpers.brandBg(0.12),
      border: `1px solid ${theme.colors.border.brandLight}`,
      borderRadius: 4, padding: "1px 5px",
      ...style,
    }}>
      CALIBRATED TO {name.toUpperCase()}
    </span>
  );
});

/**
 * Persistent "you are working on <league>" strip for a mode's content column
 * (Tier Builder, Team Builder). Left-aligned and always present — deliberately
 * NOT the OnboardingSheet, which is one-time and dismissable. Sized to be
 * noticed: the label reads under the chip, not as fine print.
 *
 * Shared so the two sites can't drift apart: `text` is the only difference.
 *
 * With no league loaded, a setup link trails the DEFAULT chip on the same line
 * (see SetupNudge) — pass onSetup + setupSource to opt a surface in.
 */
export const LeagueContextLine = React.memo(function LeagueContextLine({ text, name, className, style, onSetup, setupSource }) {
  return (
    <div className={className} style={{
      display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
      minHeight: 26, marginBottom: 8,
      fontSize: 16, fontWeight: 700, color: theme.colors.text.secondary,
      ...style,
    }}>
      <span>{text}</span>
      <LeagueChip name={name} size="lg" />
      <SetupNudge name={name} onSetup={onSetup} source={setupSource} />
    </div>
  );
});

export default LeagueChip;
